import { useEffect, useState, useContext, useMemo, useCallback } from "react";
import { Grid, Box } from "@mui/material";
import { useGetNextCaseForQueue } from "@coherehealth/qm-api";
import { startNextCase, useFeature, useParsedQueryString, useSyncQueryParamsToPath } from "@coherehealth/common";
import { useTheme } from "@material-ui/core/styles";
import PageHeader from "../common/PageHeader";
import DashboardMetrics from "./DashboardMetrics";
import DashboardCta from "./DashboardCta";
import LoadingDialog from "components/common/modals/LoadingDialog";
import { NoCaseModal } from "./NoCaseModal";
import { useSnackbar } from "notistack";
import DashboardHistory from "./DashboardHistory";
import { queueDisplayName, showSupervisorSearchConfig } from "../../util/QueueUtil";
import { UserContext } from "UserContext";
import { useAuthorized } from "Authorization";
import QueueSelectionModal from "../common/modals/QueueSelectionModal";
import DashboardTitle from "../common/DashboardTitle";
import DashboardUserHolds from "./DashboardUserHolds";

export default function DashboardPage() {
  const { activeQueue, queueView } = useContext(UserContext);
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isQMSupervisor = useAuthorized("SUPERVISOR");
  const showSearchBarAllMyWorkPages = useFeature("showSearchBarAllMyWorkPages");
  const showServiceCaseUserHolds = useFeature("showServiceCaseUserHolds");

  const allowHoldsTable = showServiceCaseUserHolds && activeQueue?.holdsEnabled;

  const [noCaseAvailable, setNoCaseAvailable] = useState<boolean>(false);
  const [displayQueueSelectionModal, setDisplayQueueSelectionModal] = useState(false);
  const qs = useParsedQueryString();
  useSyncQueryParamsToPath({ ...qs, queueId: activeQueue?.id });

  const {
    loading: serviceCaseLoadingFromQueue,
    error: getNextCaseErrorFromQueue,
    refetch: getNextCaseFromQueue,
  } = useGetNextCaseForQueue({
    queueId: "",
    lazy: true,
  });

  const fetchCase = useCallback(async () => {
    const serviceCaseFromQueue = await getNextCaseFromQueue({
      pathParams: { queueId: activeQueue?.id },
      queryParams: { timeStamp: new Date().toISOString() },
    });
    if (serviceCaseFromQueue) {
      startNextCase(
        setNoCaseAvailable,
        serviceCaseFromQueue,
        enqueueSnackbar,
        false,
        false,
        activeQueue?.id,
        activeQueue?.routeConfigByCaseType
      );
    }
  }, [activeQueue?.id, enqueueSnackbar, getNextCaseFromQueue, activeQueue?.routeConfigByCaseType]);

  useEffect(() => {
    if (getNextCaseErrorFromQueue) {
      enqueueSnackbar("Error fetching next case", {
        variant: "error",
        preventDuplicate: true,
      });
    }
  }, [enqueueSnackbar, getNextCaseErrorFromQueue]);

  const showSupervisorSearch = useMemo(() => {
    return showSearchBarAllMyWorkPages || (isQMSupervisor && showSupervisorSearchConfig(queueView, "AGENT_DASHBOARD"));
  }, [isQMSupervisor, queueView, showSearchBarAllMyWorkPages]);

  const queueName = useMemo(() => {
    return queueDisplayName(queueView, activeQueue?.displayConfig);
  }, [queueView, activeQueue?.displayConfig]);

  return (
    <>
      <Grid container sx={{ padding: theme.spacing(4, 4, 0, 4), flexDirection: "column", overflow: "hidden" }}>
        <Box>
          <DashboardTitle title={"My Work"} applyPadding={!showSupervisorSearch} />
          <PageHeader
            headerLabel={queueName}
            tertiaryButtonTestid="qm-button-change-queue"
            tertiaryButtonLabel="Change queue"
            tertiaryButtonAction={() => setDisplayQueueSelectionModal(true)}
            supervisorSearch={showSupervisorSearch}
            routeConfigByCaseType={activeQueue?.routeConfigByCaseType}
            activeQueueId={activeQueue?.id}
            guardrails={activeQueue?.guardrails}
          />
        </Box>
        <Box sx={{ my: 4 }}>
          <DashboardMetrics queueView={queueView} activeQueueId={activeQueue?.id} />
        </Box>
        <Box sx={{ my: 4 }}>
          <DashboardCta fetchCase={fetchCase} />
        </Box>
        {allowHoldsTable && (
          <Box sx={{ margin: theme.spacing(3, -1, 0, -2), display: "flex", width: "calc(100% + 24px)" }}>
            <DashboardUserHolds activeQueueId={activeQueue?.id} />
          </Box>
        )}
        <Box sx={{ margin: theme.spacing(allowHoldsTable ? 0 : 3, -1, 3, -2) }}>
          <DashboardHistory
            queueView={queueView}
            activeQueueId={activeQueue?.id}
            displayConfig={activeQueue?.displayConfig}
          />
        </Box>
      </Grid>
      <LoadingDialog
        open={serviceCaseLoadingFromQueue}
        headingText="Finding the most important case"
        subHeaderText="This may take a few seconds"
      />
      <NoCaseModal
        open={noCaseAvailable}
        setNoCaseAvailable={setNoCaseAvailable}
        retry={fetchCase}
        queueViewState={queueView}
      />
      <QueueSelectionModal
        isOpen={displayQueueSelectionModal}
        closeModal={() => setDisplayQueueSelectionModal(false)}
      />
    </>
  );
}
